/**
 * "Sync-Verlauf der letzten 14 Tage" — one stacked bar per Swiss calendar day.
 *
 * THE BACKEND OWNS THE CALENDAR, THIS FILE OWNS THE PIXELS. Every day, every
 * count and the order they arrive in come from `GET /sync-runs/daily`, which
 * buckets by Europe/Zurich, zero-fills and sorts ascending. Nothing here generates
 * a day, fills a gap, converts a timezone, groups table rows or adds up a status —
 * each of those would re-derive a rule the endpoint exists to own, and would derive
 * it in the browser's zone over whatever page of rows happens to be loaded.
 *
 * FIXED AT `DAILY_CHART_DAYS`, DELIBERATELY UNCOUPLED FROM THE TABLE FILTER. An operator can
 * set the table to 30 or 90 days; this stays "die letzten 14 Tage", because it is
 * the compact operating overview and a heading that changes meaning with a filter
 * elsewhere on the page is a heading nobody can trust. Same for the scopes: always
 * the two data syncs, so the question the bars answer never shifts.
 *
 * RUNNING IS NOT A BAR. `running_count` is a run that has not decided anything yet;
 * drawing it as green would claim a success and as red a failure. It is left out of
 * the stack rather than recoloured into a verdict it has not reached.
 *
 * COUNTS, NOT RATES. Absolute runs per day on the Y axis — no percentage, no
 * success rate. The rate that exists is computed in SQL over the KPI window above
 * and belongs there.
 *
 * THE DATE STRING IS NEVER FED TO `new Date()`. `"2026-08-06"` parsed that way is
 * midnight UTC, which renders as the 5th west of Greenwich — the exact class of bug
 * this endpoint was built to remove. It is split on the hyphens instead.
 */
import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/**
 * The three TERMINAL statuses, in stack order from the bottom up.
 *
 * German names, not field names: a legend reading `success_count` describes the
 * payload rather than the system.
 */
export const DAILY_CHART_DAYS = 14;

/**
 * Heading, aria-label and table caption, derived from the number above.
 *
 * Written once so the request and the words describing it cannot disagree: a chart
 * titled "letzte 14 Tage" that asked for 30 is a lie no test would catch by reading
 * only one of the two.
 */
export const DAILY_CHART_TITLE = `Sync-Verlauf der letzten ${DAILY_CHART_DAYS} Tage`;

export const CHART_SERIES = [
  { key: "success_count", name: "Erfolgreich", color: "#16a34a" },
  { key: "partial_count", name: "Teilweise", color: "#f59e0b" },
  { key: "error_count", name: "Fehler", color: "#dc2626" },
];

/**
 * `"2026-08-06"` -> `"06.08."` for the axis, `"06.08.2026"` for the tooltip.
 *
 * Split on hyphens rather than parsed as a date. The value is a CIVIL DATE the
 * backend already resolved in Europe/Zurich; handing it to `new Date()` would
 * reinterpret it as a UTC instant and shift it a day for any reader west of
 * Greenwich — re-introducing, in the browser, the bug the endpoint removes.
 */
export function formatBucketDate(value, { withYear = false } = {}) {
  const parts = String(value ?? "").split("-");
  if (parts.length !== 3) return String(value ?? "");
  const [y, m, d] = parts;
  return withYear ? `${d}.${m}.${y}` : `${d}.${m}.`;
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const bucket = payload[0]?.payload || {};
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-md">
      <div className="font-semibold text-foreground">
        {formatBucketDate(label, { withYear: true })}
      </div>
      {CHART_SERIES.map((s) => (
        <div key={s.key} className="mt-0.5 flex items-center gap-2 text-muted-foreground">
          <span
            aria-hidden="true"
            className="h-2 w-2 shrink-0 rounded-[2px]"
            style={{ backgroundColor: s.color }}
          />
          <span>
            {s.name}: <span className="tabular-nums text-foreground">{bucket[s.key] ?? 0}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

export default function BexioSyncRunsDailyChart({ daily, loading, error }) {
  /* Every bucket the backend sent, in the order it sent them — including the ones
     whose counts are all zero. There is no `.filter` and no sort here: dropping the
     quiet days would collapse the axis onto the busy ones and quietly redraw the
     shape of the fortnight, and re-sorting would second-guess an ORDER BY. */
  const items = Array.isArray(daily?.items) ? daily.items : [];

  return (
    <section
      aria-label={DAILY_CHART_TITLE}
      className="mt-4 rounded-xl border border-border bg-card p-4"
    >
      <h2 className="text-sm font-semibold text-foreground">{DAILY_CHART_TITLE}</h2>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Erfolgreiche, teilweise und fehlerhafte Daten-Syncs pro Tag.
      </p>

      {loading ? (
        <div
          role="status"
          aria-live="polite"
          className="mt-4 h-[260px] animate-pulse rounded-lg bg-muted"
        >
          <span className="sr-only">Sync-Verlauf wird geladen …</span>
        </div>
      ) : error ? (
        /* The chart's own failure domain: the table, the KPIs and the detail
           column above are fed by three other reads and stay usable. */
        <p role="status" className="mt-4 text-sm text-muted-foreground">
          Sync-Verlauf konnte nicht geladen werden.
        </p>
      ) : items.length === 0 ? (
        /* The endpoint returns `days` zero-buckets even for an org with no runs, so
           this is reachable only if the response is malformed. It says that,
           rather than drawing bars nobody sent. */
        <p className="mt-4 text-sm text-muted-foreground">
          Keine Sync-Verlaufsdaten verfügbar.
        </p>
      ) : (
        <>
          <div className="mt-3 h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={items} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                <CartesianGrid
                  stroke="currentColor"
                  className="text-border"
                  strokeDasharray="3 4"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v) => formatBucketDate(v)}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 10, fill: "currentColor" }}
                  className="text-muted-foreground"
                  /* Recharts drops labels rather than data when the axis is
                     narrow — every bar stays, only its caption thins out. */
                  interval="preserveStartEnd"
                  minTickGap={4}
                />
                {/* Zero-based and integer-only: a "2.5 Läufe" gridline would name
                    a thing that cannot have happened. Recharts sizes the stack
                    itself — adding the three counts up here to find a ceiling
                    would be this file tallying statuses, which is the endpoint's
                    job even when the arithmetic is only for an axis. */}
                <YAxis
                  allowDecimals={false}
                  domain={[0, "auto"]}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11, fill: "currentColor" }}
                  className="text-muted-foreground"
                />
                <Tooltip content={<ChartTooltip />} cursor={false} />
                <Legend
                  verticalAlign="bottom"
                  height={28}
                  wrapperStyle={{ fontSize: 12 }}
                />
                {CHART_SERIES.map((s, i) => (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    name={s.name}
                    stackId="runs"
                    fill={s.color}
                    /* Only the topmost segment is rounded, or the stack grows
                       notches between its parts. */
                    radius={i === CHART_SERIES.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* The same numbers as a table, for readers who do not get them from a
              picture. Same precedent as the revenue cashflow card. */}
          <table className="sr-only">
            <caption>{DAILY_CHART_TITLE}</caption>
            <thead>
              <tr>
                <th scope="col">Tag</th>
                {CHART_SERIES.map((s) => (
                  <th key={s.key} scope="col">
                    {s.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.date}>
                  <th scope="row">{formatBucketDate(b.date, { withYear: true })}</th>
                  {CHART_SERIES.map((s) => (
                    <td key={s.key}>{b[s.key] ?? 0}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
